// Centralized API Service for Rushmore Publishing

const API_ENDPOINTS = {
  GEOLOCATION: 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation',
  ADD_LEAD: 'https://leadapi.crmbytes.com/api/Lead/AddLead'
};

/**
 * Fetch user's geolocation data
 */
export const getGeolocation = async () => {
  try {
    const response = await fetch(API_ENDPOINTS.GEOLOCATION, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    return null;
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return null;
  }
};

/**
 * Extract numeric phone number from formatted string
 */
const extractNumericPhone = (phoneNumber) => {
  if (!phoneNumber) return '';
  // Remove all non-numeric characters
  return phoneNumber.replace(/\D/g, '');
};

/**
 * Get country dial code from country code
 */
const getDialCodeFromCountry = (countryCode) => {
  const dialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'AU': 61, 'IN': 91, 'PK': 92,
    'DE': 49, 'FR': 33, 'IT': 39, 'ES': 34, 'NL': 31, 'BE': 32,
    'CN': 86, 'JP': 81, 'KR': 82, 'BR': 55, 'MX': 52, 'AR': 54,
    'ZA': 27, 'NG': 234, 'EG': 20, 'SA': 966, 'AE': 971, 'TR': 90,
    'RU': 7, 'UA': 380, 'PL': 48, 'SE': 46, 'NO': 47, 'DK': 45,
    'FI': 358, 'IE': 353, 'PT': 351, 'GR': 30, 'CH': 41, 'AT': 43,
    'NZ': 64, 'SG': 65, 'MY': 60, 'TH': 66, 'VN': 84, 'PH': 63,
    'ID': 62, 'BD': 880, 'LK': 94
  };
  return dialCodes[countryCode] || 1;
};

/**
 * Submit lead to CRM
 */
export const submitLead = async (formData, geoData = null, packageInfo = null) => {
  try {
    // Extract numeric phone number
    const numericPhone = extractNumericPhone(formData.phoneNumber || formData.phone);
    
    // Determine country and dial code
    let country = formData.country || (geoData?.data?.countryCode) || 'US';
    let countryDialCode = formData.countryDialCode || getDialCodeFromCountry(country);
    
    // Build payload
    const payload = {
      name: formData.fullName || formData.name || '',
      email: formData.email || '',
      countryDialCode: parseInt(countryDialCode),
      country: country,
      phoneNumber: numericPhone,
      ipAddress: geoData?.data?.ipAddress || '',
      description: formData.message || formData.projectDetails || formData.description || '',
      pageUrl: window.location.href,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: packageInfo?.title || '',
      packageAmount: packageInfo?.amount || 0
    };

    const response = await fetch(API_ENDPOINTS.ADD_LEAD, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    // Handle different status codes
    if (response.status === 200 && result.isSuccess) {
      // Success - redirect to application page with customerId
      if (result.data?.customerId) {
        window.location.href = `/application/main?customerid=${result.data.customerId}`;
      }
      return {
        success: true,
        message: result.data?.message || 'Lead submitted successfully',
        data: result.data
      };
    } else if (response.status === 401) {
      // Unauthorized - show message from API
      return {
        success: false,
        message: result.message || 'Unauthorized access'
      };
    } else if (response.status === 400) {
      // Bad request - show errorMessage from API
      return {
        success: false,
        message: result.errorMessage || result.data?.message || 'Invalid data submitted'
      };
    } else {
      return {
        success: false,
        message: result.errorMessage || result.message || 'Something went wrong. Please try again.'
      };
    }
  } catch (error) {
    console.error('Lead Submission Error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.'
    };
  }
};

/**
 * Combined function: Get geolocation and submit lead
 * IMPORTANT: Geolocation is MANDATORY - form will not submit without it
 */
export const submitLeadWithGeo = async (formData, packageInfo = null) => {
  // First, get geolocation - this is MANDATORY
  const geoData = await getGeolocation();
  
  // Check if geolocation was successfully fetched
  if (!geoData || !geoData.isSuccess || !geoData.data?.ipAddress) {
    return {
      success: false,
      message: 'Unable to detect your location. Please check your internet connection and try again.'
    };
  }
  
  // Then submit lead with geolocation data
  return await submitLead(formData, geoData, packageInfo);
};
